import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {
  createCompany,
  ensurePersonalCompany,
  linkByCode,
} from '@/entities/session/api';
import {
  DEFAULT_ORGANIZATION_TYPE,
  ORGANIZATION_TYPES,
  organizationTypeLabelKey,
  type OrganizationType,
} from '@/entities/company/orgType';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {Screen} from '@/shared/ui/screen';
import {ChoiceChips} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Where a brand-new account lands: signed in, owning nothing, working nowhere.
 *
 * Three doors, and they are genuinely different products — a home terminal, a company
 * with a payroll, or an employee card issued by someone else. Guessing on the user's
 * behalf (say, silently creating a personal workspace at registration) would give a
 * new hire a "Home" they never asked for and hide the invite-code box they came for.
 *
 * None of the three writes to the store directly. Each calls the server, then asks the
 * store to RE-RESOLVE contexts — so the tabs that appear are the ones the backend
 * actually granted, not the ones we hoped it would.
 *
 * Visually the three doors are now one choice, not three stacked forms: each card
 * carries its own glyph, and the card the user is filling in lifts (teal outline) while
 * the other two stay quiet. The old layout gave all three an identical grey outline and
 * an identical grey button, so nothing said which one you were in the middle of.
 */
type Card3 = 'personal' | 'company' | 'invite';

const styles = StyleSheet.create({
  body: {gap: spacing(4), paddingTop: spacing(4), paddingBottom: spacing(8)},
  header: {gap: spacing(2), marginBottom: spacing(1)},
  cardHead: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  cardTitle: {flex: 1},
  card: {gap: spacing(3)},
  expanded: {gap: spacing(3)},
  field: {gap: spacing(2)},
  logout: {alignSelf: 'center', paddingVertical: spacing(4)},
});

/**
 * One door: a glyph, a title, what it gets you, and whatever it needs to be opened.
 *
 * Declared at module scope, NOT inside OnboardingScreen. A component defined inside a
 * render is a new type on every keystroke, so React would unmount and remount the
 * subtree — and the company-name field would lose focus after every character typed
 * into it.
 */
function Door({
  active,
  icon,
  title,
  description,
  children,
}: {
  active: boolean;
  icon: IconName;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  const p = useTheme();
  return (
    <Card
      padding={4}
      style={[styles.card, active && {borderColor: p.primary, borderWidth: 1}]}>
      <View style={styles.cardHead}>
        <IconBubble tone={active ? p.accent : p.muted} size={42} square>
          <Icon name={icon} size={21} color={active ? p.primary : p.foreground} />
        </IconBubble>
        <AppText variant="heading" style={styles.cardTitle}>
          {title}
        </AppText>
      </View>
      <AppText variant="caption">{description}</AppText>
      {children}
    </Card>
  );
}

export function OnboardingScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const reloadContexts = useAuthStore(s => s.reloadContexts);
  const signOut = useAuthStore(s => s.signOut);

  const [open, setOpen] = useState<Card3 | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [orgType, setOrgType] = useState<OrganizationType>(DEFAULT_ORGANIZATION_TYPE);
  const [inviteCode, setInviteCode] = useState('');
  const [busy, setBusy] = useState<Card3 | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Do the thing, then let the server tell us what we became. */
  async function run(card: Card3, action: () => Promise<unknown>) {
    setError(null);
    setBusy(card);
    try {
      await action();
      await reloadContexts();
      // On success the store's status flips to 'ready' and this screen unmounts.
    } catch (e) {
      setError(messageOf(e, t('errors.network')));
    } finally {
      setBusy(null);
    }
  }

  function toggle(card: Card3) {
    setError(null);
    setOpen(prev => (prev === card ? null : card));
  }

  return (
    <Screen scroll width="narrow" style={styles.body} testID="onboarding-screen">
      <View style={styles.header}>
        <AppText variant="display">{t('onboarding.title')}</AppText>
        <AppText variant="body" color={p.mutedForeground}>
          {t('onboarding.subtitle')}
        </AppText>
      </View>

      {/* 1 — a personal workspace. One tap: there is nothing to name. */}
      <Door
        active={open === 'personal'}
        icon="home"
        title={t('onboarding.personal.title')}
        description={t('onboarding.personal.description')}>
        <AppButton
          testID="onboarding-personal"
          title={t('onboarding.personal.action')}
          loading={busy === 'personal'}
          disabled={busy !== null && busy !== 'personal'}
          onPress={() => run('personal', ensurePersonalCompany)}
        />
      </Door>

      {/* 2 — an organisation. Needs a name, so the card opens into a field. */}
      <Door
        active={open === 'company'}
        icon="building"
        title={t('onboarding.company.title')}
        description={t('onboarding.company.description')}>
        {open === 'company' ? (
          <View style={styles.expanded}>
            <AppInput
              testID="onboarding-company-name"
              label={t('onboarding.company.nameLabel')}
              placeholder={t('onboarding.company.namePlaceholder')}
              value={companyName}
              onChangeText={setCompanyName}
              autoFocus
            />
            {/* Asked here rather than left to be corrected later, because it used to
                be neither: creation hard-coded `other` and the settings screen showed
                the sector read-only. It is a chip row, not a required step — the
                default is already selected and «Прочее» is a legitimate answer. */}
            <View style={styles.field}>
              <AppText variant="label">{t('company.organizationType')}</AppText>
              <ChoiceChips
                options={ORGANIZATION_TYPES.map(value => ({
                  value,
                  label: t(organizationTypeLabelKey(value)),
                }))}
                value={orgType}
                onChange={setOrgType}
                disabled={busy !== null}
                testID="onboarding-company-type"
              />
            </View>
            <AppButton
              testID="onboarding-company-submit"
              title={t('onboarding.company.submit')}
              loading={busy === 'company'}
              onPress={() => {
                const name = companyName.trim();
                if (!name) {
                  setError(t('onboarding.company.nameRequired'));
                  return;
                }
                void run('company', () => createCompany(name, orgType));
              }}
            />
          </View>
        ) : (
          <AppButton
            testID="onboarding-company"
            title={t('onboarding.company.action')}
            tone="soft"
            disabled={busy !== null}
            onPress={() => toggle('company')}
          />
        )}
      </Door>

      {/* 3 — an employee card someone else issued. */}
      <Door
        active={open === 'invite'}
        icon="link"
        title={t('onboarding.invite.title')}
        description={t('onboarding.invite.description')}>
        {open === 'invite' ? (
          <View style={styles.expanded}>
            <AppInput
              testID="onboarding-invite-code"
              label={t('onboarding.invite.codeLabel')}
              placeholder={t('onboarding.invite.codePlaceholder')}
              value={inviteCode}
              onChangeText={setInviteCode}
              autoCapitalize="characters"
              autoCorrect={false}
              autoFocus
            />
            <AppButton
              testID="onboarding-invite-submit"
              title={t('onboarding.invite.submit')}
              loading={busy === 'invite'}
              onPress={() => {
                const code = inviteCode.trim();
                if (!code) {
                  setError(t('onboarding.invite.codeRequired'));
                  return;
                }
                void run('invite', () => linkByCode(code));
              }}
            />
          </View>
        ) : (
          <AppButton
            testID="onboarding-invite"
            title={t('onboarding.invite.action')}
            tone="soft"
            disabled={busy !== null}
            onPress={() => toggle('invite')}
          />
        )}
      </Door>

      {error ? (
        <Banner testID="onboarding-error" tone="error" title={error} />
      ) : null}

      <Pressable
        testID="onboarding-logout"
        style={styles.logout}
        accessibilityRole="button"
        onPress={() => void signOut()}>
        <AppText variant="label">{t('onboarding.logout')}</AppText>
      </Pressable>
    </Screen>
  );
}
